<template>
  <div class="clientCampaigns">
    <div class="pageContent">
      <div class="row">
        <div class="col-sm-3">
          <client-logo :path="client.logo"/>
        </div>
        <div class="col-sm-5">
          <heading type="h1" v-if="!loading"><router-link :to="{ path: `/client/${client.hash_id}` }">{{ client.name }}'s</router-link> Campaigns</heading>
        </div>
        <div v-if="!isClient" class="col-sm-4">
          <button-cta title="confirm" class="pull-right" data-modalType="createCampaign" :onClick="onLaunchModal">
            Create Campaign
          </button-cta>
        </div>
      </div>

      <hr />

      <search-box v-model="search" name="Campaigns..." />

      <spinner v-if="loading" />

      <slot-table :data="filteredCampaigns" :columns="table.columns">

        <template slot="name" slot-scope="props">
          <span>
            <router-link :to="{ path: `/campaigns/${props.item.hash_id}` }">
              {{ props.item.name }}
            </router-link>
          </span>
        </template>

        <template slot="campaign_job_number" slot-scope="props">
          <span>
            {{ props.item.campaign_job_number }}
          </span>
        </template>

        <template slot="date_created" slot-scope="props">
          <span>{{ props.item.date_created.value | formatDate }}</span>
        </template>

        <template slot="campaign_actions" slot-scope="props">
          <button-cta  title="edit" data-modalType="editCampaign" :data-modalField="props.item.name" :data-modalId="props.item.hash_id"
                      :onClick="onLaunchModal" class="btn btn-warning btn-xs">
            <i class="fa fa-pencil" aria-hidden="true"></i></button-cta>
          <button-cta v-if="!isClient" title="clone" data-modalType="cloneCampaign" :data-modalId="props.item.hash_id"
                      :onClick="onLaunchModal" class="btn btn-success btn-xs">
            <i class="fa fa-clone" aria-hidden="true"></i>
          </button-cta>
          <button-cta v-if="!isClient" title="confirm" data-modalType="deleteCampaign" :data-modalId="props.item.hash_id"
                      :onClick="onLaunchModal" class="btn btn-danger btn-xs">
            <i class="fa fa-trash" aria-hidden="true"></i>
          </button-cta>
        </template>

        <template slot="is_archived" slot-scope="props">
          <span v-if="props.item.is_archived" class="label label-pill label-warning">
            Archived
          </span>
          <span v-else class="label label-pill label-success">
            Active
          </span>
        </template>

        <template slot="associations" slot-scope="props">
          <span class="label label-pill label-success">
            {{props.item.associations_counter}}
          </span>
        </template>

      </slot-table>
    </div>
    <portal to="globalModal">
      <modal-delete-campaign v-if="!isClient" :show="showModal.deleteCampaign" :campaignId="modalId" :clientId="client.hash_id" @close="toggleModal('deleteCampaign')" />
      <modal-clone-campaign v-if="!isClient" :show="showModal.cloneCampaign" :campaignId="modalId" :clientId="client.hash_id" @close="toggleModal('cloneCampaign')" :callback="this.updateCampaignClone" />
      <modal-create-campaign v-if="!isClient" :clientOptions="clientOptions" :show="showModal.createCampaign" @close="toggleModal('createCampaign')" />
      <modal-edit-campaign :clientOptions="clientOptions" :show="showModal.editCampaign" :currentCampaign="editCampaign" :campaignId="modalId" :field="modalField" @close="toggleModal('editCampaign')" />
    </portal>

  </div>
</template>

<script>
import Heading from '@/components/Heading/heading'
import ClientLogo from '@/components/ClientLogo/client-logo'
import ButtonCta from '@/components/Button/button'
import Spinner from '@/components/SpinnerIcon/spinner'
import SlotTable from '@/components/Table/table'
import SearchBox from '@/components/SearchBox/searchbox'
import ModalDeleteCampaign from '@/components/Modal/campaigns/modal-delete-campaign'
import ModalCloneCampaign from '@/components/Modal/campaigns/modal-clone-campaign'
import ModalCreateCampaign from '@/components/Modal/campaigns/modal-create-campaign'
import ModalEditCampaign from '@/components/Modal/campaigns/modal-edit-campaign'

import {notificationMixin} from '@/mixins/notificationMixin'
import {campaignMixin} from '@/mixins/campaignMixin'
import {clientMixin} from '@/mixins/clientMixin'
import {modalMixin} from '@/mixins/modalMixin'
import {profileMixin} from '@/mixins/profileMixin'

import { assoc, map, find, propEq } from 'ramda'

export default {
  name: 'ClientCampaigns',
  mixins: [notificationMixin, clientMixin, campaignMixin, modalMixin, profileMixin],
  components: {
    Heading,
    ClientLogo,
    Spinner,
    SlotTable,
    ButtonCta,
    ModalDeleteCampaign,
    ModalCreateCampaign,
    ModalEditCampaign,
    ModalCloneCampaign,
    SearchBox
  },
  created () {
    this.startLoading()
    this.getClient(this.clientId).then(() => {
      this.getClientCampaigns(this.clientId).then(() => {
        this.finishLoading()
      }, error => {
        this.finishLoading()
        this.addNotification('danger', error)
      })
    }, error => {
      this.addNotification('danger', error)
    })
  },
  data: () => ({
    search: '',
    table: {
      columns: {
        name: 'Campaign Name',
        campaign_job_number: 'Campaign Job Number',
        creatives: 'Creatives',
        date_created: 'Date created',
        is_archived: 'Status'
      }
    }
  }),
  computed: {
    editCampaign () {
      const campaign = find(propEq('hash_id', this.modalId), this.clientCampaigns)
      return campaign
    },
    filteredCampaigns () {
      // add clientActions key/value to data
      const formattedCampaigns = map(item => assoc('campaignsActions', true, item), this.clientCampaigns)
      return formattedCampaigns.filter(campaign => {
        return campaign.name.toLowerCase().includes(this.search.toLowerCase()) || campaign.campaign_job_number.toLowerCase().includes(this.search.toLowerCase())
      })
    },
    isClient () {
      return this.profileUser && this.profileUser.role === 6
    }
  },
  mounted () {
    if (!this.isClient) {
      this.table.columns['associations'] = 'Associations'
      this.table.columns['campaign_actions'] = ''
    } else {
      this.table.columns['campaign_actions'] = ''
    }
    this.getClients().then(() => {
      this.clientOptions = map((item) => {
        return {
          value: item.hash_id,
          name: item.name
        }
      }, this.clients)
    })
  },
  methods: {
    updateCampaignClone (hashid) {
      this.cloneCampaignStatus(hashid).then((response) => {
        if (response.status === 'pending') {
          setTimeout(() => this.updateCampaignClone(hashid), 3000)
        } else if (response.status === 'success') {
          this.getClientCampaigns(this.clientId).then(() => {
            this.addNotification('success', 'Campaign cloned successfully. (It may take up to two minutes for all your footage assets to be copied across)')
          }, error => {
            this.addNotification('danger', error)
          })
        } else if (response.status === 'error') {
          this.addNotification('danger', 'Error Cloning Campaign : ' + response.status_message)
        }
      })
    }
  }
}
</script>

<style lang="scss">
</style>



// WEBPACK FOOTER //
// src/pages/client/campaigns/client-campaigns.vue